import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@heycater/design-system';
import {
  add,
  addWeeks,
  format,
  getWeek,
  isFirstDayOfMonth,
  isSameMonth,
} from 'date-fns';
import { useRouter } from 'next/router';
import {
  CaptionProps,
  Day,
  DayPicker,
  DayProps,
  IconLeft,
  IconRight,
  Row,
  RowProps,
  useActiveModifiers,
  useDayPicker,
  useNavigation,
} from 'react-day-picker';
import styled from 'styled-components';

import 'react-day-picker/dist/style.css';

import DatePickerLayout from 'qualification/components/question_types/DatePicker/DatePickerLayout';
import DatePickerSide from 'qualification/components/question_types/DatePicker/DatePickerSide';
import { useQualification } from 'qualification/context';
import { QUALIFICATION_STATUS } from 'qualification/context/model';
import { dateQuestion } from 'qualification/schema/questions/eventDateQuestion';
import { localeToDateLocaleMapping } from 'shared/helpers/dates';

interface Props {
  hasSidebar?: boolean;
}

function DatePickerQF({ hasSidebar }: Props) {
  const router = useRouter();
  const { actions, state, answerOf } = useQualification();

  const answer = answerOf(dateQuestion.id);
  const date = answer ? new Date(answer) : undefined;
  const attributes =
    'qualification' in state
      ? dateQuestion.getAttributes({
          answers: state.qualification.answers,
          selectedCaterer: state.qualification.selectedCaterer,
        })
      : null;
  const leadTimeDays = attributes?.minDate?.leadTimeDays || 0;

  const minimumDate = add(new Date(), { days: leadTimeDays });

  const [month, setMonth] = useState(date || minimumDate);

  useEffect(() => {
    if (answerOf('lead_type') === undefined)
      actions.answerQuestion('lead_type', 'private');
  }, [answerOf]);

  if (state.status !== QUALIFICATION_STATUS.answering) {
    return null;
  }

  const handleDateSelection = (date: Date | undefined) => {
    actions.answerQuestion(dateQuestion.id, date);
  };

  const minDateMessage =
    router.locale === 'en'
      ? attributes?.minDate?.messageEn
      : attributes?.minDate?.messageDe;

  const isDateSelectable = (date: Date) => {
    try {
      dateQuestion
        .getValidator({
          answers: state.qualification.answers,
          selectedCaterer: state.qualification.selectedCaterer,
        })
        .validateSync(date); // throws if invalid
      return true;
    } catch (e) {
      return false;
    }
  };

  return (
    <DatePickerLayout
      mt={2}
      mb={3}
      hasSidebar={hasSidebar}
      dateSelector={
        <DateSelectWrapper>
          <DayPicker
            components={{
              Caption: CustomCaption,
              Row: OnlyAfterToday,
              Day: DayWithOutsideMonthLabel,
            }}
            formatters={{
              formatWeekdayName: (day, options) =>
                format(day, 'EEEEE', { locale: options?.locale }),
            }}
            mode="single"
            month={month}
            selected={date}
            onMonthChange={setMonth}
            fromDate={minimumDate}
            disabled={(date) => !isDateSelectable(date)}
            onSelect={(date) => handleDateSelection(date)}
            captionLayout="buttons"
            locale={
              router.locale
                ? localeToDateLocaleMapping[
                    router.locale as keyof typeof localeToDateLocaleMapping
                  ]
                : undefined
            }
            showOutsideDays
          />
          {minDateMessage && (
            <Box textAlign="center" mt={2}>
              <TypographyCaption>*{minDateMessage}</TypographyCaption>
            </Box>
          )}
        </DateSelectWrapper>
      }
      sideContent={<DatePickerSide />}
    />
  );
}

function DayWithOutsideMonthLabel(props: DayProps) {
  const { outside } = useActiveModifiers(props.date, props.displayMonth);
  const daypicker = useDayPicker();
  const isMonthFirstDay = isFirstDayOfMonth(props.date);

  if (outside && isMonthFirstDay) {
    return (
      <Box position="relative">
        <Box position="absolute" top="-3px" left="-3px">
          <Typography fontSize={10}>
            {format(props.date, 'MMM', { locale: daypicker.locale })}
          </Typography>
        </Box>
        <Day {...props} />
      </Box>
    );
  }

  return <Day {...props} />;
}

function CustomCaption(props: CaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const daypicker = useDayPicker();
  return (
    <Box display="flex" justifyContent="space-between" py={1}>
      <MonthNavigationButton
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
      >
        <IconLeft />
      </MonthNavigationButton>
      <Typography>
        {format(props.displayMonth, 'MMMM', { locale: daypicker.locale })}
      </Typography>
      <MonthNavigationButton
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
      >
        <IconRight />
      </MonthNavigationButton>
    </Box>
  );
}

function OnlyAfterToday(props: RowProps) {
  const today = new Date();

  const isFirstMonth = isSameMonth(today, props.displayMonth);

  if (isFirstMonth) {
    /**
     * Offset the first month, so the fromDate is shown in the first row
     */
    const firstWeekOfMonth = getWeek(props.displayMonth);
    const weekOfFirstSelectableDay = getWeek(today);
    const weekOffset = weekOfFirstSelectableDay - firstWeekOfMonth;
    const datesWithWeekOffset = props.dates.map((date) =>
      addWeeks(date, weekOffset)
    );

    return (
      <Row
        {...props}
        weekNumber={props.weekNumber + weekOffset}
        dates={datesWithWeekOffset}
      />
    );
  }

  return <Row {...props} />;
}

const MonthNavigationButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const TypographyCaption = styled(Typography)`
  color: #686d6b;
  font-size: 12px;
  line-height: 17px;
  letter-spacing: 0.4px;
`;

const DateSelectWrapper = styled.div`
  width: fit-content;
  width: 400px;
  width: fit-content;

  .rdp {
    --rdp-accent-color: ${({ theme }) => theme.palette.secondary.light};
    --rdp-background-color: #a3cb1861;
    --rdp-cell-size: 42px;

    background-color: white;
    border-radius: 8px;
    padding: ${({ theme }) => theme.spacing(2, 2)};
    margin-bottom: 4px;
  }

  .rdp-head_cell {
    font-size: 14px;
    font-weight: 400;
    padding-bottom: ${({ theme }) => theme.spacing(2)}px;
    padding-top: ${({ theme }) => theme.spacing(3)}px;
  }

  .rdp-button {
    transition: background-color 0.2s ease;
  }
  .rdp-day_outside {
    opacity: 0.8;
  }
`;

export default DatePickerQF;
